import { useState, useRef, useEffect } from 'react';
import { Disc3, ChevronDown, Check, Search } from 'lucide-react';
import { Album } from '@/types/album';
import AlbumCover from './AlbumCover';

interface AlbumScopeSelectorProps {
  albums: Album[];
  selectedAlbumId: number | null;
  onSelect: (albumId: number | null) => void;
  totalSongs: number;
}

/**
 * Album scope selector + fly-out, mirroring the desktop app. Shows the current
 * scope ("All Songs" or an album) and lets the user filter the song list by
 * album. Only rendered by the caller when one or more albums exist.
 */
export default function AlbumScopeSelector({
  albums,
  selectedAlbumId,
  onSelect,
  totalSongs,
}: AlbumScopeSelectorProps) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  const selected = albums.find((a) => a.albumId === selectedAlbumId) || null;

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const filtered = filter
    ? albums.filter((a) => a.albumName.toLowerCase().includes(filter.toLowerCase()))
    : albums;

  function choose(albumId: number | null) {
    onSelect(albumId);
    setOpen(false);
    setFilter('');
  }

  return (
    <div className="relative max-w-md" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 px-4 py-2.5 bg-surface border border-border rounded-lg text-left hover:border-text-muted transition-colors"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Disc3 size={18} className="text-primary flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-white truncate">
            {selected ? selected.albumName : 'All Songs'}
          </div>
          <div className="text-xs text-text-muted">
            {selected
              ? `${selected.songCount ?? 0} song${selected.songCount === 1 ? '' : 's'}${selected.year ? ` · ${selected.year}` : ''}`
              : `${totalSongs} song${totalSongs === 1 ? '' : 's'}`}
          </div>
        </div>
        <ChevronDown
          size={18}
          className={`text-text-muted flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="absolute left-0 right-0 mt-2 bg-surface border border-border rounded-lg shadow-lg overflow-hidden z-50">
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
              />
              <input
                type="text"
                placeholder="Filter albums..."
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="w-full pl-9 pr-3 py-2 bg-background border border-border rounded-md text-sm text-white placeholder:text-text-muted focus:outline-none focus:border-primary"
              />
            </div>
          </div>

          <div className="max-h-80 overflow-y-auto py-1">
            <button
              onClick={() => choose(null)}
              className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-surface-hover transition-colors text-left"
            >
              <div className="w-9 h-9 flex items-center justify-center bg-surface-hover rounded flex-shrink-0">
                <Disc3 size={16} className="text-text-muted" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">All Songs</div>
                <div className="text-xs text-text-muted">
                  {totalSongs} song{totalSongs === 1 ? '' : 's'}
                </div>
              </div>
              {selectedAlbumId === null && (
                <Check size={16} className="text-primary flex-shrink-0" />
              )}
            </button>

            {filtered.map((album) => (
              <button
                key={album.albumId}
                onClick={() => choose(album.albumId)}
                className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-surface-hover transition-colors text-left"
              >
                <AlbumCover
                  albumId={album.albumId}
                  coverArtUrl={album.coverArtUrl}
                  alt={album.albumName}
                  className="w-9 h-9 object-cover rounded flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{album.albumName}</div>
                  <div className="text-xs text-text-muted truncate">
                    {album.songCount ?? 0} song{album.songCount === 1 ? '' : 's'}
                    {album.year ? ` · ${album.year}` : ''}
                  </div>
                </div>
                {selectedAlbumId === album.albumId && (
                  <Check size={16} className="text-primary flex-shrink-0" />
                )}
              </button>
            ))}

            {filtered.length === 0 && (
              <div className="px-3 py-4 text-center text-sm text-text-muted">
                No albums match &ldquo;{filter}&rdquo;
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
